import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import {
  Card, Form, Button, Row, Col, Alert, Spinner, Badge, Table, Modal,
} from 'react-bootstrap';
import mapboxgl from 'mapbox-gl';
import AddressAutocomplete from '../../../Components/AddressAutocomplete';
import PricingService from '../../../Services/PricingService';
import { MAPBOX_ACCESS_TOKEN } from '../../../config/config';

import 'mapbox-gl/dist/mapbox-gl.css';

mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

function PriceCalculator({ vehicleCategories = [] }) {
  const [originAddress, setOriginAddress] = useState('');
  const [originCoords, setOriginCoords] = useState(null);
  const [destinationAddress, setDestinationAddress] = useState('');
  const [destinationCoords, setDestinationCoords] = useState(null);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [currency, setCurrency] = useState('EUR');
  const [priceModifier, setPriceModifier] = useState('standard');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [sharedInfo, setSharedInfo] = useState(null);
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [breakdownModal, setBreakdownModal] = useState({ show: false, result: null });

  // Price modifier options
  const priceModifiers = [
    { id: 'standard', label: 'Standard Price', multiplier: 1.0 },
    { id: 'last_minute', label: 'Last Minute (+5%)', multiplier: 1.05 },
    { id: 'partner', label: 'Partner Price (-5%)', multiplier: 0.95 },
    { id: 'mice', label: 'MICE Price (+10%)', multiplier: 1.10 },
  ];

  const mapContainer = useRef(null);
  const map = useRef(null);
  const originMarker = useRef(null);
  const destinationMarker = useRef(null);
  const tableRef = useRef(null);

  // Initialize map
  useEffect(() => {
    if (map.current) return undefined;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [19.0402, 47.4979], // Budapest default
      zoom: 10,
    });

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    return () => {
      if (originMarker.current) originMarker.current.remove();
      if (destinationMarker.current) destinationMarker.current.remove();
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Update origin marker
  const updateOriginMarker = useCallback((coords) => {
    if (!map.current) return;

    if (originMarker.current) {
      originMarker.current.remove();
    }

    if (coords) {
      const el = document.createElement('div');
      el.className = 'custom-marker origin-marker';
      el.innerHTML = '<i class="fa fa-map-marker-alt" style="color: #28a745; font-size: 24px;"></i>';

      originMarker.current = new mapboxgl.Marker({ element: el })
        .setLngLat([coords.lon, coords.lat])
        .setPopup(new mapboxgl.Popup({ offset: 25 }).setHTML('<strong>Pickup</strong>'))
        .addTo(map.current);
    }
  }, []);

  // Update destination marker
  const updateDestinationMarker = useCallback((coords) => {
    if (!map.current) return;

    if (destinationMarker.current) {
      destinationMarker.current.remove();
    }

    if (coords) {
      const el = document.createElement('div');
      el.className = 'custom-marker destination-marker';
      el.innerHTML = '<i class="fa fa-map-marker-alt" style="color: #dc3545; font-size: 24px;"></i>';

      destinationMarker.current = new mapboxgl.Marker({ element: el })
        .setLngLat([coords.lon, coords.lat])
        .setPopup(new mapboxgl.Popup({ offset: 25 }).setHTML('<strong>Dropoff</strong>'))
        .addTo(map.current);
    }
  }, []);

  // Fit map to show both markers
  const fitMapToBounds = useCallback(() => {
    if (!map.current || !originCoords || !destinationCoords) return;

    const bounds = new mapboxgl.LngLatBounds();
    bounds.extend([originCoords.lon, originCoords.lat]);
    bounds.extend([destinationCoords.lon, destinationCoords.lat]);

    map.current.fitBounds(bounds, {
      padding: 50,
      maxZoom: 14,
    });
  }, [originCoords, destinationCoords]);

  // Handle origin coordinate change
  const handleOriginCoordsChange = useCallback((coords) => {
    setOriginCoords(coords);
    updateOriginMarker(coords);

    if (coords && map.current) {
      map.current.flyTo({
        center: [coords.lon, coords.lat],
        zoom: 12,
      });
    }
  }, [updateOriginMarker]);

  // Handle destination coordinate change
  const handleDestinationCoordsChange = useCallback((coords) => {
    setDestinationCoords(coords);
    updateDestinationMarker(coords);

    if (coords && map.current) {
      map.current.flyTo({
        center: [coords.lon, coords.lat],
        zoom: 12,
      });
    }
  }, [updateDestinationMarker]);

  // Fit bounds when both markers are set
  useEffect(() => {
    if (originCoords && destinationCoords) {
      fitMapToBounds();
    }
  }, [originCoords, destinationCoords, fitMapToBounds]);

  const activeCategories = vehicleCategories.filter((c) => c.active === 1);

  // No auto-select - all categories are deselected by default

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategoryIds((prev) => (prev.includes(categoryId)
      ? prev.filter((id) => id !== categoryId)
      : [...prev, categoryId]));
  };

  const handleSelectAllCategories = () => {
    if (selectedCategoryIds.length === activeCategories.length) {
      setSelectedCategoryIds([]);
    } else {
      setSelectedCategoryIds(activeCategories.map((c) => c.Id));
    }
  };

  const handleCalculate = async () => {
    if (!originCoords || !destinationCoords || selectedCategoryIds.length === 0) {
      setError('Please fill in all required fields with valid addresses and select at least one vehicle category');
      return;
    }

    setLoading(true);
    setError('');
    setResults(null);
    setSharedInfo(null);

    try {
      const response = await PricingService.calculatePrice({
        origin: {
          latitude: originCoords.lat,
          longitude: originCoords.lon,
          address: originAddress,
        },
        destination: {
          latitude: destinationCoords.lat,
          longitude: destinationCoords.lon,
          address: destinationAddress,
        },
        vehicleCategoryIds: selectedCategoryIds,
        currency,
      });

      const data = response?.data?.data;
      setResults(data?.results || []);
      setSharedInfo(data?.shared_info || null);
    } catch (err) {
      const responseData = err.response?.data;
      if (responseData?.errors?.length > 0) {
        setError(responseData.errors[0].message || responseData.message);
      } else {
        setError(responseData?.message || 'Failed to calculate price');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    const numPrice = Number(price) || 0;
    return Math.round(numPrice).toLocaleString();
  };

  const currentModifier = priceModifiers.find((m) => m.id === priceModifier) || priceModifiers[0];

  const getAdjustedPrice = (basePrice) => {
    const adjusted = basePrice * currentModifier.multiplier;
    return Math.round(adjusted * 100) / 100;
  };

  // Get category details by ID
  const getCategoryDetails = (categoryId) => (
    activeCategories.find((c) => c.Id === categoryId) || {}
  );

  // Generate table data for results
  const getTableData = () => {
    if (!results || results.length === 0) return [];

    return results
      .filter((r) => !r.error)
      .map((result) => {
        const category = getCategoryDetails(result.vehicleCategoryId);
        return {
          categoryName: result.vehicleCategoryName,
          description: category.description || '-',
          maxPassengers: category.capacity || '-',
          maxLuggage: category.max_luggage || '-',
          basePrice: result.total_price,
          adjustedPrice: getAdjustedPrice(result.total_price),
          currency: result.currency,
          priceType: result.breakdown?.type || 'calculated',
          breakdown: result.breakdown,
          fullResult: result,
        };
      })
      .sort((a, b) => a.adjustedPrice - b.adjustedPrice);
  };

  // Copy table to clipboard (HTML format for email)
  const handleCopyTable = async () => {
    const tableData = getTableData();
    if (tableData.length === 0) return;

    const currencySymbol = currency === 'EUR' ? '' : ' Ft';

    // Build plain text table for clipboard
    let plainText = `Route: ${originAddress || 'Pickup'} => ${destinationAddress || 'Dropoff'}\n`;
    if (sharedInfo?.journey_distance_km) {
      plainText += `Distance: ${sharedInfo.journey_distance_km} km\n`;
    }
    plainText += `\nVehicle Category\tDescription\tMax Pax\tMax Luggage\tPrice (${currency})\n`;
    plainText += `${'-'.repeat(80)}\n`;

    tableData.forEach((row) => {
      plainText += `${row.categoryName}\t${row.description}\t${row.maxPassengers}\t${row.maxLuggage}\t${formatPrice(row.adjustedPrice)}${currencySymbol}\n`;
    });

    if (currentModifier.multiplier !== 1.0) {
      plainText += `\nPrice type: ${currentModifier.label}`;
    }

    // Build HTML table for rich paste (email clients)
    let html = '<div style="font-family: Arial, sans-serif;">';
    html += `<p><strong>Route:</strong> ${originAddress || 'Pickup'} => ${destinationAddress || 'Dropoff'}</p>`;
    if (sharedInfo?.journey_distance_km) {
      html += `<p><strong>Distance:</strong> ${sharedInfo.journey_distance_km} km</p>`;
    }
    html += '<table border="1" cellpadding="8" cellspacing="0" style="border-collapse: collapse; width: 100%;">';
    html += `<tr style="background-color: #f8f9fa;"><th>Vehicle Category</th><th>Description</th><th>Max Pax</th><th>Max Luggage</th><th>Price (${currency})</th></tr>`;

    tableData.forEach((row) => {
      html += `<tr><td>${row.categoryName}</td><td>${row.description}</td><td>${row.maxPassengers}</td><td>${row.maxLuggage}</td><td style="text-align: right;">${formatPrice(row.adjustedPrice)}${currencySymbol}</td></tr>`;
    });

    html += '</table>';
    if (currentModifier.multiplier !== 1.0) {
      html += `<p><em>Price type: ${currentModifier.label}</em></p>`;
    }
    html += '</div>';

    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([plainText], { type: 'text/plain' }),
          'text/html': new Blob([html], { type: 'text/html' }),
        }),
      ]);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {
      // Fallback to plain text
      await navigator.clipboard.writeText(plainText);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    const tableData = getTableData();
    if (tableData.length === 0) return;

    const currencySymbol = currency === 'EUR' ? '' : ' Ft';

    let csv = `Vehicle Category,Description,Max Passengers,Max Luggage,Price (${currency})\n`;
    tableData.forEach((row) => {
      const escapedDesc = `"${(row.description || '').replace(/"/g, '""')}"`;
      csv += `${row.categoryName},${escapedDesc},${row.maxPassengers},${row.maxLuggage},${formatPrice(row.adjustedPrice)}${currencySymbol}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `pricing-${originAddress?.slice(0, 20) || 'route'}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Export to Excel (simple XLSX via HTML table)
  const handleExportExcel = () => {
    const tableData = getTableData();
    if (tableData.length === 0) return;

    const currencySymbol = currency === 'EUR' ? '' : ' Ft';

    let html = '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">';
    html += '<head><meta charset="UTF-8"></head><body>';
    html += '<table border="1">';
    html += `<tr><th colspan="5" style="font-weight:bold;">Route: ${originAddress || 'Pickup'} => ${destinationAddress || 'Dropoff'}${sharedInfo?.journey_distance_km ? ` (${sharedInfo.journey_distance_km} km)` : ''}</th></tr>`;
    html += `<tr><th>Vehicle Category</th><th>Description</th><th>Max Passengers</th><th>Max Luggage</th><th>Price (${currency})</th></tr>`;

    tableData.forEach((row) => {
      html += `<tr><td>${row.categoryName}</td><td>${row.description}</td><td>${row.maxPassengers}</td><td>${row.maxLuggage}</td><td>${formatPrice(row.adjustedPrice)}${currencySymbol}</td></tr>`;
    });

    if (currentModifier.multiplier !== 1.0) {
      html += `<tr><td colspan="5"><em>Price type: ${currentModifier.label}</em></td></tr>`;
    }

    html += '</table></body></html>';

    const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `pricing-${originAddress?.slice(0, 20) || 'route'}-${new Date().toISOString().slice(0, 10)}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const tableData = getTableData();

  return (
    <Row>
      <Col lg={7}>
        <Card className="shadow-sm">
          <Card.Header className="bg-primary text-white">
            <h5 className="mb-0">
              <i className="fa fa-calculator me-2" />
              Price Calculator
            </h5>
          </Card.Header>
          <Card.Body>
            {error && (
              <Alert variant="danger" onClose={() => setError('')} dismissible>
                {error}
              </Alert>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <i className="fa fa-map-marker-alt text-success me-2" />
                    Pickup Location *
                  </Form.Label>
                  <AddressAutocomplete
                    value={originAddress}
                    onChange={setOriginAddress}
                    onCoordinatesChange={handleOriginCoordsChange}
                    placeholder="Enter pickup address..."
                  />
                  {originCoords && (
                    <Form.Text className="text-success">
                      <i className="fa fa-check me-1" />
                      Location selected
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <i className="fa fa-map-marker-alt text-danger me-2" />
                    Dropoff Location *
                  </Form.Label>
                  <AddressAutocomplete
                    value={destinationAddress}
                    onChange={setDestinationAddress}
                    onCoordinatesChange={handleDestinationCoordsChange}
                    placeholder="Enter dropoff address..."
                  />
                  {destinationCoords && (
                    <Form.Text className="text-success">
                      <i className="fa fa-check me-1" />
                      Location selected
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="d-flex justify-content-between align-items-center">
                    <span>
                      <i className="fa fa-car me-2" />
                      Vehicle Categories *
                    </span>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={handleSelectAllCategories}
                    >
                      {selectedCategoryIds.length === activeCategories.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </Form.Label>
                  <div className="border rounded p-2" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                    {activeCategories.map((cat) => (
                      <Form.Check
                        key={cat.Id}
                        type="checkbox"
                        id={`cat-${cat.Id}`}
                        label={(
                          <small>
                            {cat.name}
                            {' '}
                            <span className="text-muted">
                              (
                              {cat.capacity}
                              {' '}
                              pax,
                              {' '}
                              {cat.max_luggage}
                              {' '}
                              bags)
                            </span>
                          </small>
                        )}
                        checked={selectedCategoryIds.includes(cat.Id)}
                        onChange={() => handleCategoryToggle(cat.Id)}
                        className="mb-1"
                      />
                    ))}
                  </div>
                  <Form.Text className="text-muted">
                    {selectedCategoryIds.length}
                    {' '}
                    of
                    {' '}
                    {activeCategories.length}
                    {' '}
                    selected
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <i className="fa fa-euro-sign me-2" />
                    Currency
                  </Form.Label>
                  <Form.Select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                  >
                    <option value="EUR">EUR (Euro)</option>
                    <option value="HUF">HUF (Hungarian Forint)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>
                <i className="fa fa-tags me-2" />
                Price Type
              </Form.Label>
              <div className="d-flex flex-wrap gap-2">
                {priceModifiers.map((mod) => (
                  <Form.Check
                    key={mod.id}
                    type="radio"
                    id={`price-mod-${mod.id}`}
                    name="priceModifier"
                    label={mod.label}
                    checked={priceModifier === mod.id}
                    onChange={() => setPriceModifier(mod.id)}
                    className="border rounded px-3 py-2"
                    style={{
                      backgroundColor: priceModifier === mod.id ? '#e7f1ff' : 'transparent',
                      borderColor: priceModifier === mod.id ? '#0d6efd' : '#dee2e6',
                    }}
                  />
                ))}
              </div>
            </Form.Group>

            <div className="d-grid">
              <Button
                variant="primary"
                size="lg"
                onClick={handleCalculate}
                disabled={
                  loading || !originCoords || !destinationCoords || selectedCategoryIds.length === 0
                }
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Calculating...
                  </>
                ) : (
                  <>
                    <i className="fa fa-calculator me-2" />
                    Calculate Prices
                  </>
                )}
              </Button>
            </div>

            {/* Results Table */}
            {results && results.length > 0 && (
              <div className="mt-4">
                {/* Shared Route Info */}
                {sharedInfo?.journey_distance_km && (
                  <Alert variant="info" className="mb-3">
                    <i className="fa fa-route me-2" />
                    <strong>Distance:</strong>
                    {' '}
                    {sharedInfo.journey_distance_km}
                    {' '}
                    km
                  </Alert>
                )}

                {/* Warnings from any result */}
                {results.some((r) => r.warnings?.length > 0) && (
                  <Alert variant="warning">
                    <i className="fa fa-exclamation-triangle me-2" />
                    {results.find((r) => r.warnings?.length > 0)?.warnings?.join(', ')}
                  </Alert>
                )}

                {/* Pricing Table */}
                <Card>
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <strong>
                      <i className="fa fa-table me-2" />
                      Pricing Comparison
                    </strong>
                    <div className="d-flex gap-2">
                      <Button
                        size="sm"
                        variant={copySuccess ? 'success' : 'outline-secondary'}
                        onClick={handleCopyTable}
                        disabled={tableData.length === 0}
                      >
                        <i className={`fa ${copySuccess ? 'fa-check' : 'fa-copy'} me-1`} />
                        {copySuccess ? 'Copied!' : 'Copy'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={handleExportCSV}
                        disabled={tableData.length === 0}
                      >
                        <i className="fa fa-file-csv me-1" />
                        CSV
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-success"
                        onClick={handleExportExcel}
                        disabled={tableData.length === 0}
                      >
                        <i className="fa fa-file-excel me-1" />
                        Excel
                      </Button>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <Table striped bordered hover responsive className="mb-0" ref={tableRef}>
                      <thead className="table-light">
                        <tr>
                          <th>Vehicle Category</th>
                          <th>Description</th>
                          <th className="text-center">Max Pax</th>
                          <th className="text-center">Max Luggage</th>
                          <th className="text-end">
                            Price (
                            {currency}
                            )
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {tableData.map((row) => (
                          <tr key={row.categoryName}>
                            <td>
                              <strong>{row.categoryName}</strong>
                              {row.priceType === 'predefined' && (
                                <Badge bg="success" className="ms-2">Fixed</Badge>
                              )}
                              <Button
                                size="sm"
                                variant="link"
                                className="p-0 ms-2"
                                onClick={() => setBreakdownModal({
                                  show: true,
                                  result: row.fullResult,
                                })}
                                title="View price breakdown"
                              >
                                <i className="fa fa-info-circle text-info" />
                              </Button>
                            </td>
                            <td className="text-muted small">{row.description}</td>
                            <td className="text-center">{row.maxPassengers}</td>
                            <td className="text-center">{row.maxLuggage}</td>
                            <td className="text-end">
                              <strong>
                                {currency === 'EUR' ? '' : ''}
                                {formatPrice(row.adjustedPrice)}
                                {currency === 'EUR' ? '' : ' Ft'}
                              </strong>
                              {currentModifier.multiplier !== 1.0 && (
                                <div className="small text-muted">
                                  Base:
                                  {' '}
                                  {formatPrice(row.basePrice)}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                  {currentModifier.multiplier !== 1.0 && (
                    <Card.Footer className="text-muted small">
                      <i className="fa fa-info-circle me-1" />
                      Prices shown with
                      {' '}
                      {currentModifier.label}
                      {' '}
                      modifier (
                      {currentModifier.multiplier > 1 ? '+' : ''}
                      {Math.round((currentModifier.multiplier - 1) * 100)}
                      %)
                    </Card.Footer>
                  )}
                </Card>

                {/* Errors for specific categories */}
                {results.filter((r) => r.error).map((errorResult) => (
                  <Alert key={errorResult.vehicleCategoryId} variant="danger" className="mt-2 small">
                    <strong>
                      {errorResult.vehicleCategoryName}
                      :
                    </strong>
                    {' '}
                    {errorResult.error}
                  </Alert>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>
      <Col lg={5} className="mt-4 mt-lg-0">
        <Card className="shadow-sm sticky-map-panel">
          <Card.Header className="bg-secondary text-white">
            <h6 className="mb-0">
              <i className="fa fa-map me-2" />
              Route Preview
            </h6>
          </Card.Header>
          <Card.Body className="p-0">
            <div
              ref={mapContainer}
              style={{ height: '500px', borderRadius: '0 0 8px 8px' }}
            />
          </Card.Body>
          {(originCoords || destinationCoords) && (
            <Card.Footer className="bg-light">
              <small>
                {originCoords && (
                  <div className="text-success">
                    <i className="fa fa-map-marker-alt me-2" />
                    Pickup set
                  </div>
                )}
                {destinationCoords && (
                  <div className="text-danger">
                    <i className="fa fa-map-marker-alt me-2" />
                    Dropoff set
                  </div>
                )}
              </small>
            </Card.Footer>
          )}
        </Card>
      </Col>

      {/* Price Breakdown Modal */}
      <Modal
        show={breakdownModal.show}
        onHide={() => setBreakdownModal({ show: false, result: null })}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fa fa-calculator me-2" />
            Price Breakdown:
            {' '}
            {breakdownModal.result?.vehicleCategoryName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {breakdownModal.result && (
            <div>
              {/* Price Type Badge */}
              <div className="mb-3">
                <Badge bg={breakdownModal.result.breakdown?.type === 'predefined' ? 'success' : 'primary'} className="me-2">
                  {breakdownModal.result.breakdown?.type === 'predefined' ? 'Fixed Route Price' : 'Calculated Price'}
                </Badge>
                {breakdownModal.result.breakdown?.predefined_route_name && (
                  <span className="text-muted">
                    Route:
                    {' '}
                    {breakdownModal.result.breakdown.predefined_route_name}
                  </span>
                )}
              </div>

              {/* Predefined Route - Simple display */}
              {breakdownModal.result.breakdown?.type === 'predefined' && (
                <Alert variant="success">
                  <strong>Fixed Price Route</strong>
                  <p className="mb-0 mt-2">
                    This route has a predefined fixed price of
                    {' '}
                    <strong>
                      {formatPrice(breakdownModal.result.total_price)}
                      {breakdownModal.result.currency === 'EUR' ? ' EUR' : ' Ft'}
                    </strong>
                  </p>
                </Alert>
              )}

              {/* Calculated Route - Detailed breakdown */}
              {breakdownModal.result.breakdown?.type === 'calculated' && (
                <>
                  {/* Distance Summary */}
                  <Card className="mb-3">
                    <Card.Header className="bg-light py-2">
                      <strong>
                        <i className="fa fa-road me-2" />
                        Distance Summary
                      </strong>
                    </Card.Header>
                    <Card.Body className="py-2">
                      <Table size="sm" className="mb-0">
                        <tbody>
                          {breakdownModal.result.breakdown.deadhead_to_pickup_km > 0 && (
                            <tr>
                              <td>Deadhead (base to pickup)</td>
                              <td className="text-end">
                                {breakdownModal.result.breakdown.deadhead_to_pickup_km}
                                {' '}
                                km
                              </td>
                            </tr>
                          )}
                          <tr>
                            <td>Journey distance</td>
                            <td className="text-end">
                              {breakdownModal.result.breakdown.journey_distance_km}
                              {' '}
                              km
                            </td>
                          </tr>
                          {breakdownModal.result.breakdown.return_to_base_km > 0 && (
                            <tr>
                              <td>Return to base</td>
                              <td className="text-end">
                                {breakdownModal.result.breakdown.return_to_base_km}
                                {' '}
                                km
                              </td>
                            </tr>
                          )}
                          <tr className="table-secondary fw-bold">
                            <td>Total billable distance</td>
                            <td className="text-end">
                              {breakdownModal.result.breakdown.total_distance_km}
                              {' '}
                              km
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>

                  {/* Band Breakdown */}
                  {breakdownModal.result.breakdown.band_breakdown?.length > 0 && (
                    <Card className="mb-3">
                      <Card.Header className="bg-light py-2">
                        <strong>
                          <i className="fa fa-layer-group me-2" />
                          Price Bands
                        </strong>
                      </Card.Header>
                      <Card.Body className="py-2">
                        <Table size="sm" className="mb-0">
                          <thead>
                            <tr>
                              <th>Band</th>
                              <th className="text-center">Distance</th>
                              <th className="text-center">Rate</th>
                              <th className="text-end">Subtotal</th>
                            </tr>
                          </thead>
                          <tbody>
                            {breakdownModal.result.breakdown.band_breakdown.map((band) => (
                              <tr key={band.band_name}>
                                <td>{band.band_name}</td>
                                <td className="text-center">
                                  {band.distance_km}
                                  {' '}
                                  km
                                </td>
                                <td className="text-center">
                                  {band.flat_rate_eur || band.flat_rate_huf ? (
                                    <span className="text-muted">flat rate</span>
                                  ) : (
                                    <span>
                                      {breakdownModal.result.currency === 'EUR'
                                        ? `${band.rate_eur} EUR/km`
                                        : `${band.rate_huf} Ft/km`}
                                    </span>
                                  )}
                                </td>
                                <td className="text-end">
                                  {formatPrice(band.subtotal)}
                                  {breakdownModal.result.currency === 'EUR' ? '' : ' Ft'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  )}

                  {/* Service Area Fees */}
                  {breakdownModal.result.breakdown.service_area_fees?.length > 0 && (
                    <Card className="mb-3">
                      <Card.Header className="bg-light py-2">
                        <strong>
                          <i className="fa fa-map-marker-alt me-2" />
                          Service Area Fees
                        </strong>
                      </Card.Header>
                      <Card.Body className="py-2">
                        <Table size="sm" className="mb-0">
                          <tbody>
                            {breakdownModal.result.breakdown.service_area_fees.map((fee) => (
                              <tr key={fee.service_area_name}>
                                <td>{fee.service_area_name}</td>
                                <td className="text-end">
                                  +
                                  {formatPrice(fee.fee)}
                                  {breakdownModal.result.currency === 'EUR' ? '' : ' Ft'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  )}

                  {/* Price Multiplier */}
                  {breakdownModal.result.breakdown.multiplier
                    && breakdownModal.result.breakdown.multiplier !== 1 && (
                    <Alert variant="info" className="py-2">
                      <i className="fa fa-percentage me-2" />
                      Price multiplier:
                      {' '}
                      <strong>
                        {breakdownModal.result.breakdown.multiplier}
                        x
                      </strong>
                      {breakdownModal.result.breakdown.multiplier_source && (
                        <>
                          {' '}
                          (
                          {breakdownModal.result.breakdown.multiplier_source}
                          )
                        </>
                      )}
                    </Alert>
                  )}

                  {/* Base Location Info */}
                  {breakdownModal.result.breakdown.base_location_name && (
                    <div className="text-muted small">
                      <i className="fa fa-home me-1" />
                      Dispatched from:
                      {' '}
                      {breakdownModal.result.breakdown.base_location_name}
                    </div>
                  )}
                </>
              )}

              {/* Warnings */}
              {breakdownModal.result.warnings?.length > 0 && (
                <Alert variant="warning" className="mt-3 mb-0">
                  <i className="fa fa-exclamation-triangle me-2" />
                  {breakdownModal.result.warnings.join(', ')}
                </Alert>
              )}

              {/* Final Price */}
              <div className="mt-4 p-3 bg-light rounded text-center">
                <h4 className="mb-0">
                  Total:
                  {' '}
                  <strong className="text-primary">
                    {formatPrice(breakdownModal.result.total_price)}
                    {breakdownModal.result.currency === 'EUR' ? ' EUR' : ' Ft'}
                  </strong>
                </h4>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setBreakdownModal({ show: false, result: null })}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Row>
  );
}

export default PriceCalculator;
